import { Snackbar } from "react-redux-snackbar";
import { Redirect, Route, Switch, Link, RouteComponentProps, withRouter } from "react-router-dom";
import { Container } from "reactstrap";
import React, { Dispatch } from "react";
import { AppThemeProvider, PrimaryButton, ContextMenuManager } from "@cadmakers-ui/core";
import { AppContext, CMApp, Panel, isDesktop, ContentPanel } from "cm-layout";
import { connect } from "react-redux";
import { User } from "oidc-client";
import { AuthRoutes, ProtectedRoute, authService, ProtectedContent } from "../../Auth";
import { AppRoutes } from "../../App";
import { FeedbackRoutes } from "../../Feedback";
import { TagRoutes } from "../../Tags";
import { appService } from "../../App/Services/AppService";
import { SubscriptionState } from "../../App/Constants/applicationEnums";
import { UserRoutes } from "../../User";
import { CompanyRoutes } from "../../Company";
import { SearchRoutes } from "../../Search";
import { ErrorBoundary, appPaths, IAppState, ResponseError, LoadingState } from "../../Common";
import { GeneralErrorPage, NotFoundPage, UnauthorizedPage } from "../../ErrorPages";
import { BreadcrumbData } from "../../Layout";
import { Application } from "../../App/Models/Application";
import {
  getApplications,
  setLoadingCompany,
  getCompany,
  getTopics,
  setLoadingTopics,
  setLoading,
  GetApplicationsArguments,
  setBreadcrumbs,
  setPath
} from "../Actions/StartupActions";
import { ErrorHandling } from "../../Auth/Components/ProtectedContent";
import { Company } from "../../Company/Models/Company";
import { Topic } from "../../Topic/Models/Topic";
import TopicRoutes from "../../Topic/Components/TopicRoutes";
import { FeedbackParent } from "../../Feedback/Constants/feedbackEnums";
import HomePageContainer from "../../Home/Components/HomePageContainer";
import WorkflowRoutes from "../../Workflows/Components/WorkflowRoutes";
import InitialTagsLoader from "./InitialTagsLoader";
import { FeedbackNavbar } from "./FeedbackNavBar";
import { FeedbackSidebar } from "./FeedbackSidebar";
import { TopbarProvider } from "../Hooks/useTopbarContext";

import "../../Layout/Styles/Layout.scss";
import { BreadcrumbProvider } from "../Hooks/useBreadcrumbs";
import styles from "../Styles/App.module.scss";
import { SearchbarContext } from "../../Search/SearchbarContext";
import { SearchContext } from "../../Layout/Models/SearchContext";
import OverviewRoutes from "../../Overview/Components/OverviewRoutes";

type StateProps = {
  parentType: FeedbackParent;
  user?: User;
  isAuthenticated: boolean;
  email: string;
  username: string;
  appSubState: Record<string, SubscriptionState>;
  activeApp?: Application;
  activeTopic?: Topic;
  appsLoadingState: LoadingState;
  appsLoadingError?: ResponseError;
  company?: Company;
  companyLoadingError?: ResponseError;
  companyLoadingState: LoadingState;
  topics: Topic[];
  topicsLoadingError?: ResponseError;
  topicsLoadingState: LoadingState;
  breadcrumbs: BreadcrumbData[];
  path?: string;
};

type State = {
  shouldReload: Boolean;
  context?: SearchContext;
  searchQuery: string;
};

type DispatchProps = {
  getApplications: (args: GetApplicationsArguments) => void;
  setLoadingApps: () => void;
  getCompany: (username: string, callback: (company: Company) => void) => void;
  setLoadingCompany: () => void;
  getTopics: (shortname: string) => void;
  setLoadingTopics: () => void;
  setPath: (path: string) => void;
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  parentType: state.app.parentType,
  user: state.oidc.user,
  isAuthenticated: !!state.oidc.user && !state.oidc.user.expired,
  email: !!state.oidc.user && state.oidc.user.profile.email ? state.oidc.user.profile.email : "",
  username: !!state.oidc.user && state.oidc.user.profile.username,
  appSubState: appService.getApplicationSubStatesFromAppState(state),
  activeApp: state.app.activeApp,
  activeTopic: state.app.activeTopic,
  appsLoadingState: state.app.appsLoadingState,
  appsLoadingError: state.app.appsLoadingError,
  company: state.app.company,
  companyLoadingState: state.app.companyLoadingState,
  companyLoadingError: state.app.companyLoadingError,
  topics: state.app.topics,
  topicsLoadingError: state.app.topicsLoadingError,
  topicsLoadingState: state.app.topicsLoadingState,
  breadcrumbs: state.app.breadcrumbs,
  path: state.app.path
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getApplications: (args: GetApplicationsArguments) => dispatch(getApplications(args)),
  setLoadingApps: () => dispatch(setLoading()),
  getCompany: (username: string, callback: (company: Company) => void) =>
    dispatch(getCompany(username, callback)),
  setLoadingCompany: () => dispatch(setLoadingCompany()),
  getTopics: (shortname: string) => dispatch(getTopics(shortname)),
  setLoadingTopics: () => dispatch(setLoadingTopics()),
  setPath: (path: string) => dispatch(setPath(path)),
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => dispatch(setBreadcrumbs(breadcrumbs))
});

class App extends React.Component<RouteComponentProps & StateProps & DispatchProps, State> {
  constructor(props: RouteComponentProps & StateProps & DispatchProps) {
    super(props);

    this.state = {
      shouldReload: false,
      searchQuery: ""
    };

    this.setReloadFeedback = this.setReloadFeedback.bind(this);
  }

  componentDidMount = () => {
    this.fetchData();
  };

  componentDidUpdate = () => {
    if (this.props.path !== this.props.location.pathname) {
      this.props.setPath(this.props.location.pathname);
    }

    this.fetchData();
  };

  private fetchData = () => {
    if (this.props.isAuthenticated) {
      if (!this.props.appsLoadingState.isLoaded && !this.props.appsLoadingState.isLoading) {
        this.props.setLoadingApps();
        this.props.getApplications({ appSubStates: this.props.appSubState });
      }

      if (!this.props.companyLoadingState.isLoaded && !this.props.companyLoadingState.isLoading) {
        this.props.setLoadingCompany();
        this.props.getCompany(this.props.username, (company: Company) => {
          this.props.setLoadingTopics();
          this.props.getTopics(company.shortName);
        });
      }
    }
  };

  onClickAddFeedback = () => {
    let type = "feature";

    if (this.props.location.pathname.includes("/bugreports")) {
      type = "bug";
    } else if (this.props.location.pathname.includes("/resourcerequests")) {
      type = "resourcerequest";
    }

    let route = `${appPaths.feedback.submitFeedback}?type=${type}`;

    if (this.props.activeApp) {
      route += `&app=${this.props.activeApp.rolePrefix}`;
    } else if (this.props.activeTopic) {
      route += `&topic=${this.props.activeTopic.code}`;
    }

    this.props.history.push(route);
  };

  getAddFeedbackURL = () => {
    const defAppPrefix = "IM";
    let route = "";

    if (this.props.activeApp) {
      route = appPaths.app.addFeedback;
      route = route.replace(":id", this.props.activeApp.rolePrefix);
    } else if (this.props.activeTopic) {
      route = appPaths.topic.addFeedback;
      route = route.replace(":topicCode", this.props.activeTopic.code);
    } else {
      route = route.replace(":id", defAppPrefix);
    }

    let type = "feature";

    if (this.props.location.pathname.includes("/bugreports")) {
      type = "bug";
    } else if (this.props.location.pathname.includes("/resourcerequests")) {
      type = "resourcerequest";
    }

    route += `?type=${type}`;
    return route;
  };

  setReloadFeedback = (reload: Boolean, context?: SearchContext, query?: string) => {
    this.setState({
      shouldReload: reload
    });

    if (context) {
      this.setState({
        context
      });
    }

    if (query) {
      this.setState({
        searchQuery: query
      });
    }
  };

  render() {
    return (
      <AppContext.Provider value={CMApp.CMFeedback}>
        <SearchbarContext.Provider
          value={{
            shouldReload: this.state.shouldReload,
            context: this.state.context,
            searchQuery: this.state.searchQuery,
            setReloadFeedback: this.setReloadFeedback
          }}
        >
          <AppThemeProvider app="cmFeedback">
            <ContextMenuManager>
              <BreadcrumbProvider>
                <ProtectedContent errorHandling={ErrorHandling.ShowNothing}>
                  <InitialTagsLoader />
                </ProtectedContent>
                <Panel flow="column" fill translate={{}}>
                  <FeedbackNavbar {...this.props} onClickAddFeedback={this.onClickAddFeedback} />
                  <Panel fill translate={{}}>
                    {isDesktop && <FeedbackSidebar {...this.props} />}
                    <Panel flow="column" fill style={{ backgroundColor: "#f8f8f8" }} translate={{}}>
                      <TopbarProvider>
                        {/* {isDesktop && <FeedbackTopBar />} */}
                        <Switch>
                          <Route path={appPaths.overview.root} component={OverviewRoutes} />
                          <ContentPanel translate={{}} style={{ paddingTop: "10px" }}>
                            <ErrorBoundary>
                              {this.props.location.pathname !== appPaths.feedback.submitFeedback &&
                                this.props.location.pathname !== appPaths.overview.root && (
                                  <Link to={this.getAddFeedbackURL()}>
                                    <div className={styles.addFeedbackButtonContainer}>
                                      <PrimaryButton
                                        type="generic"
                                        text="Add Feedback"
                                        onClick={() => {}}
                                      />
                                    </div>
                                  </Link>
                                )}
                              <Switch>
                                <Route path={appPaths.auth.root} component={AuthRoutes} />
                                <Route path={appPaths.company.root} component={CompanyRoutes} />
                                <Route path={appPaths.feedback.root} component={FeedbackRoutes} />
                                <Route path={appPaths.search.root} component={SearchRoutes} />
                                <ProtectedRoute
                                  path={appPaths.root}
                                  exact
                                  component={HomePageContainer}
                                />
                                <Route
                                  path={appPaths.generalError}
                                  exact
                                  component={GeneralErrorPage}
                                />
                                <Route path={appPaths.notFound} exact component={NotFoundPage} />
                                <Route
                                  path={appPaths.unauthorized}
                                  exact
                                  component={UnauthorizedPage}
                                />
                                <Route path={appPaths.user.root} component={UserRoutes} />
                                <Route path={appPaths.topic.root} component={TopicRoutes} />
                                <Route path={appPaths.workflows.root} component={WorkflowRoutes} />
                                <Route path={appPaths.tags.root} component={TagRoutes} />
                                <Route path={appPaths.app.root} component={AppRoutes} />
                                <Redirect to={appPaths.notFound} />
                              </Switch>
                            </ErrorBoundary>
                          </ContentPanel>
                        </Switch>
                      </TopbarProvider>
                    </Panel>
                  </Panel>
                  {/* This component is needed to display snackbars in the app */}
                  <div style={{ zIndex: 99999 }}>
                    <Snackbar />
                  </div>
                </Panel>
              </BreadcrumbProvider>
            </ContextMenuManager>
          </AppThemeProvider>
        </SearchbarContext.Provider>
      </AppContext.Provider>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
